import { ActionsUnion, createAction } from '@housinganywhere/safe-redux';
import { Thunk } from 'ha/myredux/types';
import { UserInformation } from '@ha/api/v2/types/UserInformation';
import { notEmpty } from 'ha/utils/notEmpty';

import {
  getFromStorage,
  setInStorage,
  removeFromStorage,
} from 'ha/utils/storage';

import {
  BILLING_INFO_STORAGE_KEY,
  SHOW_WE_WANT_TO_PAY_STORAGE_KEY,
} from 'ha/modules/WeWantToPay/constants';
import { showWeWantToPay } from 'ha/modules/WeWantToPay/selectors';
import { FetchUserBillingInfoTypes } from 'ha/modules/AuthLogic/constants';
import { ValidateBillingDataResponse } from '@ha/api/v2/payments/validateBillingInformation';

export const Actions = {
  start: () => createAction(FetchUserBillingInfoTypes.start),
  done: (data: UserInformation) =>
    createAction(FetchUserBillingInfoTypes.done, data),
  fail: (error: Error) => createAction(FetchUserBillingInfoTypes.fail, error),
};
export type ActionsType = ActionsUnion<typeof Actions>;

const fetchAndCacheBillingInfo =
  (userId: number): Thunk =>
  (dispatch, getState, { apiV2 }) => {
    dispatch(Actions.start());

    return Promise.all<
      { data: UserInformation },
      { data: ValidateBillingDataResponse }
    >([
      apiV2.getUserInformation('me', {
        expand: 'savedPayoutMethodsCount,advertiserBookingsCount,isKYCDone',
      }),
      apiV2.validateBillingInformation(userId),
    ])
      .then(
        ([
          { data: userInformationFromHa },
          {
            data: { hasCompleteBillingInformation },
          },
        ]) => {
          const userInformation = {
            ...userInformationFromHa,
            hasCompleteBillingInformation,
          };

          dispatch(Actions.done(userInformation));

          setInStorage(BILLING_INFO_STORAGE_KEY, userInformation);
          setInStorage(
            SHOW_WE_WANT_TO_PAY_STORAGE_KEY,
            showWeWantToPay(getState()),
          );
        },
      )
      .catch(error => {
        dispatch(Actions.fail(error));

        removeFromStorage(BILLING_INFO_STORAGE_KEY);
        removeFromStorage(SHOW_WE_WANT_TO_PAY_STORAGE_KEY);
      });
  };

// Fetch user billing information and cache it.
// - If WeWantToPay should be displayed, fetch info from API (invalidating
//   cache is not possible when adding payout methods).
// - If WeWantToPay should NOT be displayed, use cache, if user doesn't have
//   bookings check that first to potentially invalidate cache.
// - Cache values are invalidated when adding/removing payout methods,
//   and when saving billing information.
//
// This is done to avoid calling API for data that need to be fetched from other
// microservice ('savedPayoutMethodsCountd' & 'hasCompleteBillingInformation').

export const fetchUserBillingInfo: () => Thunk =
  () =>
  (dispatch, getState, { apiV2 }) => {
    const { authLogic } = getState();

    if (!authLogic.user) {
      return;
    }

    const userId = authLogic.user.id;

    const data: UserInformation | undefined = getFromStorage(
      BILLING_INFO_STORAGE_KEY,
    );
    const show: boolean | undefined = getFromStorage(
      SHOW_WE_WANT_TO_PAY_STORAGE_KEY,
    );
    const noCache = !notEmpty(data) || !notEmpty(show);

    if (noCache || show) {
      return dispatch(fetchAndCacheBillingInfo(userId));
    }

    if (data && data.advertiserBookingsCount > 0) {
      dispatch(Actions.done(data));

      return Promise.resolve();
    }

    return apiV2
      .getUserInformation('me', { expand: 'advertiserBookingsCount' })
      .then(res => {
        if (data && res.data && res.data.advertiserBookingsCount === 0) {
          dispatch(Actions.done(data));

          return;
        }

        // eslint-disable-next-line consistent-return
        return dispatch(fetchAndCacheBillingInfo(userId));
      })
      .catch(() => dispatch(fetchAndCacheBillingInfo(userId)));
  };
